import { PoiStatus, PrismVolume } from '@/__generated__/graphql';
import { DEFAULT_VISIBILITY_BOX, VBOX_ANTIFLICKER_SCALE_INCREASE } from '@/constants';
import { CuboidBase } from '@/modules/cuboids/types';
import { AnalysisCuboid } from '@/types';
import { Number3, VisibilityBoxMode, VisibilityBoxProps } from '@abyss/3d-viewer';

export const calculatePointOnRayWithDistance = (
  rayOrigin: Number3,
  lookAt: Number3,
  distance: number
): Number3 => {
  const [ox, oy, oz] = rayOrigin;
  const [tx, ty, tz] = lookAt;
  const dx = tx - ox;
  const dy = ty - oy;
  const dz = tz - oz;
  const scale = distance / Math.hypot(dx, dy, dz);
  return [ox + dx * scale, oy + dy * scale, oz + dz * scale];
};

export const calculateDistance3d = (a: Number3, b: Number3): number => {
  const [ax, ay, az] = a;
  const [bx, by, bz] = b;
  const dx = ax - bx;
  const dy = ay - by;
  const dz = az - bz;
  const distance = Math.hypot(dx, dy, dz);
  return distance;
};

export const toNumber3 = (array: number[]): Number3 => {
  if (array.length !== 3) {
    throw new Error('toNumber3: array length is not 3');
  }
  return array as Number3;
};

export function calculateBoundingBox(points: number[][]): {
  center: [number, number];
  width: number;
  height: number;
} {
  // Find min/max coordinates
  const xs = points.map((p) => p[0]);
  const ys = points.map((p) => p[1]);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);

  // Calculate center
  const centerX = (minX + maxX) / 2;
  const centerY = (minY + maxY) / 2;

  // Calculate dimensions
  const width = maxX - minX;
  const height = maxY - minY;

  return {
    center: [centerX, centerY],
    width,
    height,
  };
}

export function prismToAnalysisCuboid({
  prismData,
  id,
  poiId,
  name,
  status,
}: {
  prismData: PrismVolume;
  id: string;
  poiId?: string;
  name?: string;
  status?: PoiStatus;
}): AnalysisCuboid {
  // Calculate bounding box for the xy polygon
  const boundingBox = calculateBoundingBox(prismData.xy_polygon);

  // Calculate z center and height
  const zCenter = (prismData.z_range[0] + prismData.z_range[1]) / 2;
  const zHeight = prismData.z_range[1] - prismData.z_range[0];

  // Create the cuboid
  const cuboid: AnalysisCuboid = {
    id,
    position: [boundingBox.center[0], boundingBox.center[1], zCenter],
    rotation: [0, 0, 0],
    scale: [boundingBox.width, boundingBox.height, zHeight],
    ...(name && { name }),
    ...(poiId && { poiId }),
    status,
  };

  return cuboid;
}

/**
 * Converts an AnalysisCuboid to VisibilityBoxProps
 *
 * @param cuboid The source AnalysisCuboid object
 * @param mode The VisibilityBoxMode to use in the result
 * @param step Default step values
 * @param scaleIncrease Allows to increase scale optionally (could increase scale by 0.0001 or so to avoid flickering)
 * @returns VisibilityBoxProps representing the same 3D region
 */
export function convertAnalysisCuboidToVisibilityBoxProps(
  cuboid: CuboidBase,
  mode = VisibilityBoxMode.Enabled,
  step = DEFAULT_VISIBILITY_BOX.step,
  scaleIncrease: number | undefined = undefined
): VisibilityBoxProps {
  const { position, rotation, scale } = cuboid;
  const [sx, sy, sz] = scale;
  return {
    mode,
    position,
    rotation,
    scale: scaleIncrease ? [sx + scaleIncrease, sy + scaleIncrease, sz + scaleIncrease] : scale,
    step,
  };
}

export function convertCuboidToAdjustedVisibilityBoxProps(
  cuboid: CuboidBase,
  mode = VisibilityBoxMode.Enabled,
  step = DEFAULT_VISIBILITY_BOX.step
): VisibilityBoxProps {
  return convertAnalysisCuboidToVisibilityBoxProps(
    cuboid,
    mode,
    step,
    VBOX_ANTIFLICKER_SCALE_INCREASE
  );
}
